/**
 * Reference HTML renderer.
 *
 * This is intentionally the least interesting file in the package. It
 * knows fourteen block types and eight inline types, contains no
 * conditionals about rent negotiation or notice periods, and will need
 * changing only when the IR gains a node — which should be rare. That
 * small, boring surface is the compatibility problem we traded the
 * per-template React components for.
 *
 * It emits semantic class names only; every visual decision lives in a
 * theme stylesheet. A consumer that would rather render with React, LaTeX
 * or a DOCX writer implements the same switch over the same nodes.
 */
import { numberAffixes } from '../ir/nodes.js';
import { SIGNING_ROW_LABELS, signMeasureToken, signatureCaption } from './signing.js';
export const RENDERER = { name: 'regta-document-ir/html', version: '1' };
/** The legacy lane's unfilled-value placeholder (its `PLACEHOLDER`). */
export const BLANK_PLACEHOLDER = '____________';
const BLANK_TEXT = {
    short: '____',
    medium: BLANK_PLACEHOLDER,
    long: '_'.repeat(24),
    full: '_'.repeat(96),
};
/** The underscore run printed for an unfilled value of `width`. */
export const blankText = (width) => BLANK_TEXT[width ?? 'medium'];
export function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
const attrs = (pairs) => pairs
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => ` ${k}="${escapeHtml(String(v))}"`)
    .join('');
export function createHtmlRenderer(options = {}) {
    const prefix = options.prefix ?? 'rd';
    const sectionSymbol = options.sectionSymbol ?? '§ ';
    const sectionSuffix = options.sectionSuffix ?? '';
    const attachmentWord = options.attachmentWord ?? 'Bilaga';
    const blankMode = options.blank ?? 'text';
    // Point-numbered documents ("4.") print the number run in with the title,
    // as the förhandlingsordning family always has; "§ 4" sits in a gutter.
    const runIn = sectionSuffix === '.';
    const track = options.track === true;
    const cn = (...parts) => parts.filter(Boolean).map((p) => `${prefix}-${p}`).join(' ');
    const hints = (node) => {
        const h = node;
        const edit = track ? node.edit : undefined;
        return attrs([
            ['data-instance-id', h.instanceId],
            ['data-keep-together', h.keepTogether ? 'true' : null],
            ['data-keep-with-next', h.keepWithNext ? 'true' : null],
            ['data-page-break-before', h.pageBreakBefore ? 'true' : null],
            ['data-node-address', edit?.address],
            ['data-fingerprint', edit?.fingerprint],
            ['data-instance-fingerprint', edit?.instanceFingerprint],
            ['data-change', edit?.change],
            ['data-patch-id', edit?.patchId],
            ['data-moved-by', edit?.movedBy],
            ['data-locked', edit?.locked ? 'true' : null],
            ['data-layer', edit?.layer],
            ['data-suggestion', edit?.suggestion],
        ]);
    };
    const changeAttrs = (edit) => attrs([
        ['data-patch-id', edit?.patchId],
        ['data-layer', edit?.layer],
        ['data-suggestion', edit?.suggestion],
    ]);
    const ins = (html, edit, block = false) => `<ins class="${cn('ins', block && 'ins--block', edit?.layer && `ins--${edit.layer}`)}"${changeAttrs(edit)}>${html}</ins>`;
    const del = (html, edit, block = false) => `<del class="${cn('del', block && 'del--block', edit?.layer && `del--${edit.layer}`)}"${changeAttrs(edit)}>${html}</del>`;
    /** Track-mode attributes of a part (row, option, column header, caption). */
    const partAttrs = (edit) => track && edit
        ? attrs([
            ['data-node-address', edit.address],
            ['data-fingerprint', edit.fingerprint],
            ['data-change', edit.change],
            ['data-patch-id', edit.patchId],
            ['data-locked', edit.locked ? 'true' : null],
            ['data-layer', edit.layer],
            ['data-suggestion', edit.suggestion],
        ])
        : '';
    /** A label run, with its before-text in track mode. */
    const editedLabel = (content, edit) => {
        if (!track || edit?.change !== 'edited' || !edit.before)
            return inlines(content);
        return del(inlines(edit.before), edit) + ins(inlines(content), edit);
    };
    /** Change classes of a part's own element, as block ghosts / inserts get. */
    const partChange = (edit) => track && edit?.change === 'removed' ? 'del del--block' : track && edit?.change === 'inserted' ? 'ins ins--block' : false;
    /** Inline content of a paragraph / heading, with its before-text in track mode. */
    const editedInlines = (node) => {
        const edit = track ? node.edit : undefined;
        if (edit?.change !== 'edited' || !edit.before)
            return inlines(node.content);
        return del(inlines(edit.before), edit) + ins(inlines(node.content), edit);
    };
    const inline = (node) => {
        switch (node.type) {
            case 'text':
                return escapeHtml(node.text);
            case 'strong':
                return `<strong>${node.content.map(inline).join('')}</strong>`;
            case 'emphasis':
                return `<em>${node.content.map(inline).join('')}</em>`;
            case 'lineBreak':
                return '<br />';
            case 'label':
                return `<span class="${cn('label')}">${node.content.map(inline).join('')}</span>`;
            case 'image': {
                // A print header ignores stylesheet widths, so the size bound rides
                // inline (see ImageNode). `object-fit:contain` keeps a logo's aspect.
                const size = [
                    node.maxHeight ? `max-height:${node.maxHeight}` : null,
                    node.maxWidth ? `max-width:${node.maxWidth}` : null,
                    node.maxHeight || node.maxWidth ? 'object-fit:contain' : null,
                ]
                    .filter(Boolean)
                    .join(';');
                return `<img class="${cn('image')}"${attrs([['src', node.src], ['alt', node.alt ?? ''], ['style', size || null]])} />`;
            }
            case 'value':
                return node.text == null
                    ? `<span class="${cn('blank', `blank--${node.blankWidth ?? 'medium'}`, blankMode === 'rule' && 'blank--rule')}"${attrs([['data-key', node.sourceKey]])}>${blankMode === 'rule' ? '' : blankText(node.blankWidth)}</span>`
                    : `<span class="${cn('value', node.text.includes('\n') && 'value--multiline')}"${attrs([['data-format', node.format], ['data-key', node.sourceKey]])}>${escapeHtml(node.text)}</span>`;
            case 'sectionRef':
            case 'attachmentRef':
            case 'documentRef':
                return `<span class="${cn('ref', `ref--${node.type}`)}"${attrs([['data-target', node.target]])}>${escapeHtml(node.text ?? node.target)}</span>`;
            default:
                return '';
        }
    };
    const inlines = (list) => list.map(inline).join('');
    const sectionTitle = (node) => {
        const edit = track ? node.edit : undefined;
        const title = escapeHtml(node.title ?? '');
        if (edit?.change !== 'edited' || edit.beforeTitle == null)
            return title;
        return del(escapeHtml(edit.beforeTitle), edit) + ins(title, edit);
    };
    const section = (node, depth) => {
        const level = Math.min(depth + 1, 6);
        const heading = node.title
            ? `<h${level} class="${cn('section__heading', runIn && 'section__heading--run-in')}">${node.number
                ? `<span class="${cn('section__number')}">${escapeHtml(depth === 1 ? `${sectionSymbol}${node.number}${sectionSuffix}` : node.number)}</span>`
                : ''}<span class="${cn('section__title')}">${sectionTitle(node)}</span></h${level}>`
            : '';
        return `<section class="${cn('section', `section--depth-${depth}`, runIn && depth === 1 && 'section--run-in')}"${attrs([
            ['id', `section-${node.ref}`],
            ['data-ref', node.ref],
            ['data-number', node.number],
        ])}${hints(node)}${attrs([['data-variant', node.variant]])}>${heading}<div class="${cn('section__body')}">${blocks(node.content, depth + 1)}</div></section>`;
    };
    const block = (node, depth) => {
        const change = node.edit?.change;
        if (change === 'removed')
            return track ? del(blockInner(node, depth), node.edit, true) : '';
        if (track && change === 'inserted')
            return ins(blockInner(node, depth), node.edit, true);
        return blockInner(node, depth);
    };
    const blockInner = (node, depth) => {
        switch (node.type) {
            case 'section':
                return section(node, depth);
            case 'heading':
                return `<h${node.level} class="${cn('heading', node.align === 'center' && 'heading--center')}"${hints(node)}>${editedInlines(node)}</h${node.level}>`;
            case 'paragraph':
                return `<p class="${cn('p', node.variant && node.variant !== 'body' && `p--${node.variant}`, node.align && node.align !== 'left' && `p--${node.align}`)}"${hints(node)}>${editedInlines(node)}</p>`;
            case 'list': {
                const tag = node.ordered ? 'ol' : 'ul';
                return `<${tag} class="${cn('list')}"${hints(node)}>${node.items
                    .map((i) => `<li class="${cn('list__item')}">${blocks(i.content, depth)}</li>`)
                    .join('')}</${tag}>`;
            }
            case 'optionList':
                return `<ul class="${cn('options', node.select === 'single' && 'options--single', node.layout === 'inline' && 'options--inline')}"${hints(node)}${attrs([['data-key', node.sourceKey]])}>${node.items
                    .map((i) => `<li class="${cn('option', i.checked && 'option--checked')}"${attrs([['data-code', i.code]])}${partAttrs(i.edit)}><span class="${cn('option__box')}" aria-hidden="true"${attrs([['data-key', i.sourceKey ?? node.sourceKey]])}>${i.checked ? '&#9746;' : '&#9744;'}</span><span class="${cn('option__label')}">${editedLabel(i.content, i.edit)}</span>${i.children ? `<div class="${cn('option__children')}">${blocks(i.children, depth)}</div>` : ''}</li>`)
                    .join('')}</ul>`;
            case 'table': {
                const head = node.columns.some((c) => c.header?.length)
                    ? `<thead><tr>${node.columns
                        .map((c) => `<th class="${cn('table__th')}"${attrs([['style', c.width ? `width:${c.width}%` : null], ['data-align', c.align]])}${partAttrs(c.edit)}>${editedLabel(c.header ?? [], c.edit)}</th>`)
                        .join('')}</tr></thead>`
                    : '';
                const body = `<tbody>${node.rows
                    .map((r) => `<tr class="${cn('table__tr', r.variant === 'total' && 'table__tr--total')}">${r.cells
                    .map((c, index) => `<td class="${cn('table__td')}"${attrs([
                    ['colspan', c.colSpan && c.colSpan > 1 ? c.colSpan : null],
                    ['data-align', node.columns[index]?.align],
                ])}>${blocks(c.content, depth)}</td>`)
                    .join('')}</tr>`)
                    .join('')}</tbody>`;
                const caption = node.caption?.length
                    ? `<caption class="${cn('table__caption')}"${partAttrs(node.captionEdit)}>${editedLabel(node.caption, node.captionEdit)}</caption>`
                    : '';
                // Column widths hold with or without a header row.
                const cols = node.columns.some((c) => c.width)
                    ? `<colgroup>${node.columns.map((c) => `<col${attrs([['style', c.width ? `width:${c.width}%` : null]])} />`).join('')}</colgroup>`
                    : '';
                // Width rides inline: a print header/footer ignores stylesheet widths.
                return `<table class="${cn('table', node.variant && `table--${node.variant}`)}" style="width:100%"${hints(node)}>${caption}${cols}${head}${body}</table>`;
            }
            case 'detailList':
                return `<dl class="${cn('details', node.variant === 'boxed' && 'details--boxed', node.columns === 2 && 'details--cols-2')}"${hints(node)}>${node.items
                    .filter((i) => track || i.edit?.change !== 'removed')
                    .map((i) => {
                    const e = track ? i.edit : undefined;
                    const value = e?.change === 'edited' && e.beforeDescription
                        ? del(blocks(e.beforeDescription, depth), e, true) + ins(blocks(i.description, depth), e, true)
                        : blocks(i.description, depth);
                    return `<div class="${[cn('details__row'), partChange(e) && cn(...partChange(e).split(' '))].filter(Boolean).join(' ')}"${partAttrs(e)}><dt class="${cn('details__term')}">${editedLabel(i.term, e)}</dt><dd class="${cn('details__value')}">${value}</dd></div>`;
                })
                    .join('')}</dl>`;
            case 'signature':
                return signatureBlock(node);
            case 'attachmentList':
                return `<ul class="${cn('attachments')}"${hints(node)}>${(node.entries ?? [])
                    .map((e) => `<li class="${cn('attachments__item')}"${attrs([['data-id', e.id]])}><span class="${cn('attachments__number')}">${escapeHtml(e.number != null ? `${attachmentWord} ${e.number}` : attachmentWord)}</span><span class="${cn('attachments__label')}">${escapeHtml(e.label)}</span></li>`)
                    .join('')}</ul>`;
            case 'notice':
                return `<aside class="${cn('notice', `notice--${node.tone ?? 'info'}`)}"${hints(node)}>${node.title ? `<p class="${cn('notice__title')}">${escapeHtml(node.title)}</p>` : ''}${blocks(node.content, depth)}</aside>`;
            case 'group':
                return `<div class="${cn('group', `group--${node.variant ?? 'plain'}`)}"${hints(node)}>${node.title ? `<p class="${cn('group__title')}">${escapeHtml(node.title)}</p>` : ''}${node.variant === 'columns'
                    ? `<div class="${cn('group__columns')}">${node.content
                        .map((child) => `<div class="${cn('group__column')}">${block(child, depth)}</div>`)
                        .join('')}</div>`
                    : blocks(node.content, depth)}</div>`;
            case 'spacer':
                return `<div class="${cn('spacer', `spacer--${node.size ?? 'md'}`)}"${hints(node)}></div>`;
            case 'divider':
                return `<hr class="${cn('divider')}"${hints(node)} />`;
            case 'pageBreak':
                return `<div class="${cn('page-break')}"${hints(node)}></div>`;
            default:
                return '';
        }
    };
    // A run of `sideBySide` nodes joins the row of the node before it.
    const blocks = (input, depth = 1) => {
        const list = track ? input : input.filter((n) => n.edit?.change !== 'removed');
        let out = '';
        for (let i = 0; i < list.length;) {
            let end = i + 1;
            while (end < list.length && list[end].sideBySide)
                end++;
            const run = list.slice(i, end);
            out +=
                run.length === 1
                    ? block(run[0], depth)
                    : `<div class="${cn('row', `row--${run.length}`)}">${run
                        .map((n) => `<div class="${cn('row__cell')}">${block(n, depth)}</div>`)
                        .join('')}</div>`;
            i = end;
        }
        return out;
    };
    const measure = options.signing?.measure === true;
    // Black, absolutely positioned, zero layout impact: the measurement pass
    // has the same geometry as the clean one, so the coordinates read from
    // it are valid for the document actually sent.
    const marker = (panel, field) => measure && panel.role
        ? `<span class="${cn('sign-marker')}" aria-hidden="true" style="position:absolute;left:0;bottom:0;margin:0;padding:0;font-size:5px;line-height:5px;color:#000;white-space:nowrap;font-family:monospace">${escapeHtml(signMeasureToken(panel.role, panel.ordinal ?? 0, panel.signerIndex ?? 0, field))}</span>`
        : '';
    const row = (label, panel, field) => `<div class="${cn('signature__row')}"${attrs([['data-sign-field', field]])}><span class="${cn('signature__row-label')}">${escapeHtml(label)}:</span><span class="${cn('signature__blank')}">${field ? marker(panel, field) : ''}</span></div>`;
    const signaturePanel = (panel) => {
        const placeDate = options.signing?.placeDate ?? panel.showPlaceDate !== false;
        const lines = [
            panel.name ? `<p class="${cn('signature__name')}">${escapeHtml(`${SIGNING_ROW_LABELS.name}: ${panel.name}`)}</p>` : '',
            panel.legalId ? `<p class="${cn('signature__legal-id')}">${escapeHtml(`${SIGNING_ROW_LABELS.legalId}: ${panel.legalId}`)}</p>` : '',
            panel.entity ? `<p class="${cn('signature__entity')}">${escapeHtml(panel.entity)}</p>` : '',
            panel.capacity ? `<p class="${cn('signature__capacity')}">${escapeHtml(panel.capacity)}</p>` : '',
        ].join('');
        return (`<div class="${cn('signature__panel')}"${attrs([
            ['data-party', panel.partyRef],
            ['data-anchor', panel.anchor],
            ['data-sign-role', panel.role],
            ['data-sign-ordinal', panel.role ? (panel.ordinal ?? 0) : null],
            ['data-signer', panel.role ? (panel.signerIndex ?? 0) : null],
        ])}>` +
            `<div class="${cn('signature__main')}">` +
            `<div class="${cn('signature__line')}" data-sign-field="signature">${marker(panel, 'signature')}</div>` +
            `<p class="${cn('signature__caption')}">${escapeHtml(signatureCaption(panel.label, panel.signerIndex ?? 0, panel.signerCount ?? 1))}</p>` +
            lines +
            '</div>' +
            (panel.name ? '' : row(SIGNING_ROW_LABELS.name, panel, null)) +
            (placeDate ? row(SIGNING_ROW_LABELS.place, panel, 'place') + row(SIGNING_ROW_LABELS.date, panel, 'date') : '') +
            '</div>');
    };
    const signatureBlock = (node) => `<div class="${cn('signature', `signature--cols-${node.columns ?? 2}`)}"${hints(node)}>` +
        (node.statement?.length ? `<p class="${cn('signature__statement')}">${inlines(node.statement)}</p>` : '') +
        (node.heading ? `<p class="${cn('signature__heading')}">${escapeHtml(node.heading)}</p>` : '') +
        `<div class="${cn('signature__panels')}">${node.panels.map(signaturePanel).join('')}</div></div>`;
    return { blocks, block, inline, inlines };
}
/**
 * Renders each part into the header/body/footer fragments a PDF pipeline
 * wants. Numbering style comes off the document unless the caller overrides
 * it, so a resolved IR renders correctly with no other context.
 */
export function renderDocumentHtml(document, options = {}) {
    const affixes = numberAffixes(document.numberStyle);
    const renderer = createHtmlRenderer({
        ...options,
        sectionSymbol: options.sectionSymbol ?? affixes.prefix,
        sectionSuffix: options.sectionSuffix ?? affixes.suffix,
    });
    return {
        renderer: { ...RENDERER },
        parts: document.parts.map((part) => ({
            key: part.key,
            title: part.title,
            letterhead: part.letterhead?.length ? renderer.blocks(part.letterhead) : null,
            body: renderer.blocks(part.body),
            footer: part.footer?.length ? renderer.blocks(part.footer) : null,
        })),
    };
}
export function renderPrintDocument(document, options = {}) {
    const rendered = renderDocumentHtml(document, options);
    const prefix = options.prefix ?? 'rd';
    const region = (kind, html) => `<div class="${prefix}-doc ${prefix}-region ${prefix}-region--${kind}">${html}</div>`;
    return {
        renderer: rendered.renderer,
        parts: rendered.parts.map((part) => ({
            key: part.key,
            title: part.title,
            letterhead: part.letterhead == null ? null : region('letterhead', part.letterhead),
            body: region('body', part.body),
            footer: part.footer == null ? null : region('footer', part.footer),
        })),
    };
}
/** A standalone, self-contained HTML page — preview and visual snapshots. */
export function renderStandaloneHtml(document, options = {}) {
    const rendered = renderDocumentHtml(document, options);
    const prefix = options.prefix ?? 'rd';
    const body = rendered.parts
        .map((part) => `<article class="${prefix}-part${options.page ? ` ${prefix}-page` : ''}" data-part="${escapeHtml(part.key)}">` +
        (part.letterhead ? `<header class="${prefix}-letterhead ${prefix}-region--letterhead">${part.letterhead}</header>` : '') +
        `<main class="${prefix}-body ${prefix}-region--body">${part.body}</main>` +
        (part.footer ? `<footer class="${prefix}-footer ${prefix}-region--footer">${part.footer}</footer>` : '') +
        '</article>')
        .join('');
    return [
        '<!doctype html>',
        `<html lang="${escapeHtml(document.locale)}"><head><meta charset="utf-8" />`,
        `<title>${escapeHtml(document.title)}</title>`,
        options.stylesheet ? `<style>${options.stylesheet}</style>` : '',
        `</head><body class="${prefix}-doc">${body}</body></html>`,
    ].join('');
}
//# sourceMappingURL=html.js.map